/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: MPL-2.0
 */

import {
  ConfigApi,
  DiscoveryApi,
  createApiRef,
} from '@backstage/core-plugin-api';
import {
  Resource,
  StateVersion,
  TerraformApi,
  TerraformOutput,
  TerraformRun,
  Workspace,
  RunResponse,
  WorkspaceRequest,
  VariablesResponse,
  VariableResponse,
  VariableRequest,
  RunRequest,
  TeamResponse,
  TeamTokenRequest,
  TeamTokenResponse,
  TeamRequest,
  TeamsResponse,
  OrganizationResponse,
  OrgTokenResponse,
  TeamTokenVaultResponse,
  BackstageCredentials,
  BackstageCredentialsResponse,
  OrgDetailResponse
} from './types';
import { AuthResponse, GlobalSecretResponse, TerraformSecretResponse } from '@internal/plugin-scaffolder-vault-backend/src/api/types';


const DEFAULT_PROXY_PATH = '/terraform/api';
const DEFAULT_TERRAFORM_URL = 'https://app.terraform.io';

/**
 * @public
 */
export const terraformApiRef = createApiRef<TerraformApi>({
  id: 'plugin.terraform.service',
});

type Options = {
  discoveryApi: DiscoveryApi;
  configApi: ConfigApi;
};

export class TerraformClient implements TerraformApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly configApi: ConfigApi;
  private readonly proxyPath: string;
  private readonly targetUrl: string;

  constructor(options: Options) {
    this.discoveryApi = options.discoveryApi;
    this.configApi = options.configApi;

    const proxyPath = options.configApi.getOptionalString(
      'terraform.proxyPath',
    );
    this.proxyPath = proxyPath ?? DEFAULT_PROXY_PATH;

    const targetUrl =
      options.configApi.getOptionalString('terraform.baseUrl') ||
      DEFAULT_TERRAFORM_URL;
    this.targetUrl = targetUrl;
  }

  async getSecrets(key:string, tenantId:any): Promise<any> {
    const baseUrl = await this.discoveryApi.getBaseUrl('scaffolder');
    
    const res = await fetch(`${baseUrl}/terraform/credentials?key=${encodeURIComponent(key)}&tenantId=${encodeURIComponent(tenantId)}`
    );
    if (!res.ok) throw new Error('Failed to fetch secrets');
    const data = await res.json(); 
    return data;
  }

  async saveSecrets(updatedData: any): Promise<any> {
    const baseUrl = await this.discoveryApi.getBaseUrl('scaffolder');
    console.log(`UPADATE DATA---- > ${updatedData}`)
    const res = await fetch(`${baseUrl}/terraform/saveCredentialsInDB`, {
      method: 'POST', // must be POST to send data
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedData), // send the data as JSON
    });
  
    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`Failed to save secrets: ${res.status} ${errText}`);
    }
  
    const data = await res.json();
    return data;
  }

  private async enableTerraformEngine(vaultProxyUrl: string, headers: Headers, terraformMount: string, orgToken: string, tfCloudTeamId: string) {
    await fetch(`${vaultProxyUrl}/v1/sys/mounts/${terraformMount}`, {
      headers: headers,
      method: 'POST',
      body: JSON.stringify({ type: 'terraform' })
    });

    await fetch(`${vaultProxyUrl}/v1/${terraformMount}/config`, {
      headers: headers,
      method: 'POST',
      body: JSON.stringify({ token: orgToken })
    });

    await fetch(`${vaultProxyUrl}/v1/${terraformMount}/role/backstage`, {
      headers: headers,
      method: 'POST',
      body: JSON.stringify({ team_id: tfCloudTeamId })
    });
  }

  private async vaultSetup(vaultRootToken: string, orgToken: string, tfCloudTeamId: string, terraformMount: string) {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const vaultProxyUrl = `${proxyUrl}/vault`;
    const vaultHeaders = new Headers();
    vaultHeaders.append("X-Vault-Token", `${vaultRootToken}`);
    vaultHeaders.append("Content-Type", "application/json");
    this.enableTerraformEngine(vaultProxyUrl, vaultHeaders, terraformMount, orgToken, tfCloudTeamId);
  }

  private async getVaultToken(): Promise<string> {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const vaultProxyUrl = `${proxyUrl}/vault`;
    const githubToken = this.configApi.getOptionalString('terraform.githubToken');
    const vaultPayload = JSON.stringify({ token: `${githubToken}` });
    const vaultResponse = await fetch(`${vaultProxyUrl}/v1/auth/github/login`, {
      method: 'POST',
      body: vaultPayload
    });
    return vaultResponse.json().then((resp: AuthResponse) => {
      return resp.auth.client_token;
    });
  }

  private async getTerraformToken(): Promise<string> {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const vaultProxyUrl = `${proxyUrl}/vault`;
    const vaultHeaders = new Headers();
    const vaultToken = await this.getVaultToken();
    vaultHeaders.append("X-Vault-Token", `${vaultToken}`);
    const response = await fetch(`${vaultProxyUrl}/v1/terraform/creds/backstage`, {
      method: 'GET',
      headers: vaultHeaders,
    });
    return response.json().then((resp: TerraformSecretResponse) => {
      return resp.data.token;
    })
  }

  private async getTerraformTeamToken(): Promise<string> {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const vaultProxyUrl = `${proxyUrl}/vault`;
    const vaultHeaders = new Headers();
    const vaultToken = await this.getVaultToken();
    vaultHeaders.append("X-Vault-Token", `${vaultToken}`);
    const response = await fetch(`${vaultProxyUrl}/v1/aws-secret/creds`, {
      method: 'GET',
      headers: vaultHeaders,
    });
    return response.json().then((resp: GlobalSecretResponse) => {
      return resp.data.team_token;
    })
  }

  private async saveTerraformToken(tfTeamToken: string, teamID: string) {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const vaultProxyUrl = `${proxyUrl}/vault`;
    const vaultHeaders = new Headers();
    const vaultToken = await this.getVaultToken();
    vaultHeaders.append("X-Vault-Token", `${vaultToken}`);
    vaultHeaders.append("Content-Type", "application/json");
    const response = await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
      method: 'GET',
      headers: vaultHeaders,
    });
    const data = await response.json().then((resp: GlobalSecretResponse) => {
      return resp.data;
    });
    data['team_token'] = tfTeamToken;
    data['team_id'] = teamID;
    await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
      method: 'POST',
      headers: vaultHeaders,
      body: JSON.stringify(data)
    })

  }

  private async getHeaders(key:string, tenantId:any): Promise<Record<string, string>> {
    // const token = await this.getTerraformToken();
    // const headers = {
    //   'Content-Type': 'application/vnd.api+json'
    // };

    // if (token != undefined) {
    //   headers['Authorization'] = `Bearer ${token}`;
    // }
    const data = await this.getSecrets(key, tenantId);
    const headers= {
      'Content-Type': 'application/vnd.api+json',
      "Authorization": `Bearer ${data.data}`
    } 

    return headers;
  }

  private generateHeaders(orgToken: string): Record<string, string> {
    const headers = {
      'Content-Type': 'application/vnd.api+json',
      'Authorization': `Bearer ${orgToken}`
    };

    return headers;
  }

  private async getUrls() {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    return {
apiUrl: `${proxyUrl}${this.proxyPath}/v2`,
      baseUrl: `${proxyUrl}${this.proxyPath}`,
    };
  }

  async getTargetUrl() {
    return this.targetUrl;
  }

  async getWorkspace(
    organization: string,
    workspaceName: string,
    tenantId:any
  ): Promise<Workspace> {
    
    const { apiUrl } = await this.getUrls();
    const newHeader = await this.getHeaders('org_token', tenantId) 
    console.log(newHeader, "new header-------------------------")
    const response = await fetch(
      `${apiUrl}/organizations/${organization}/workspaces/${workspaceName}`,
      {
        method: 'GET',
        headers:  await this.getHeaders('org_token', tenantId) 
      }
    );

    if (!response.ok) {
      throw new Error(
        `failed to fetch data, status ${response.status}: ${response.statusText}`,
      );
    }

    const data = await response.json(); // read body only once
    return data;
  }

  async listRuns(workspaceID: string, tenantId:any): Promise<TerraformRun[]> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/workspaces/${workspaceID}/runs`, {
      method: 'GET',
      headers: await this.getHeaders('org_token', tenantId) 
    });

    if (!response.ok) {
      throw new Error(
        `failed to fetch data, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json().then(runs => {
      return runs.data;
    });
  }

  private async getCurrentStateVersion(
    workspaceID: string,
    tenantId:any
  ): Promise<StateVersion> {
    const { apiUrl } = await this.getUrls();
    const response = await fetch(
      `${apiUrl}/workspaces/${workspaceID}/current-state-version`,
      {
        method: 'GET',
        headers: await this.getHeaders('org_token', tenantId) 
      },
    );

    if (!response.ok) {
      throw new Error(
        `failed to fetch data, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json();
  }

  private async getStateVersionOutput(
    outputID: string,
    tenantId:any
  ): Promise<TerraformOutput> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(
      `${apiUrl}/state-version-outputs/${outputID}`,
      {
        method: 'GET',
        headers: await this.getHeaders('org_token',tenantId) 
      },
    );

    if (!response.ok) {
      throw new Error(
        `failed to fetch data, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json().then(output => {
      return output.data;
    });
  }

  async getResources(workspaceID: string,  tenantId:any): Promise<Resource[]> {
    const stateVersion = await this.getCurrentStateVersion(workspaceID, tenantId);
    return (await stateVersion).data.attributes.resources;
  }

  async getOutputs(workspaceID: string): Promise<TerraformOutput[]> {
    const stateVersion = this.getCurrentStateVersion(workspaceID);
    const stateVersionOutputs = (await stateVersion).data.relationships.outputs
      .data;

    return Promise.all(
      stateVersionOutputs.map(async output => {
        const out = await this.getStateVersionOutput(output.id);
        return out;
      }),
    );
  }

  async deleteWorkspace(organization: string, workspace: string, tenantId:any) {
    const { apiUrl } = await this.getUrls();
    const headers = await this.getHeaders('org_token', tenantId) 
    const response = await fetch(
      `${apiUrl}/organizations/${organization}/workspaces/${workspace}`,
      {
        method: 'DELETE',
        headers: headers,
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to delete workspace, status ${response.status}: ${response.statusText}`,
      );
    }
  }

  async deleteComponent(component: string) {
    const baseUrl = await this.discoveryApi.getBaseUrl("catalog")
    const response = await fetch(`${baseUrl}/entities\?filter\=kind\=component`, {
      method: 'GET',
    })
    const locationRes = await fetch(`${baseUrl}/locations`, {
      method: 'GET',
    })
    if (response.ok) {
      const data = await response.json();
      const locationData = await locationRes.json();
      const entity = data.find((e: { metadata: { name: string; }; }) => e.metadata.name === component)

      console.log(entity.metadata.annotations["backstage.io/view-url"])
      const gitUrl = entity.metadata.annotations["backstage.io/view-url"]
      const location = locationData.find((e: { data: { target: string; }; }) => e.data.target === gitUrl)
      await fetch(`${baseUrl}/locations/${location.data.id}`, {
        method: 'DELETE',
      })
    } else {
      const errorMessage = await response.text();
      throw new Error(`Failed to delete repository: ${response.status} - ${errorMessage}`);
    }
  }

  /**
 * This asynchronous function fetches a component by its service type from the Backstage catalog.
 * The service type can be either 'terraform' or 'repository'.
 *
 * @async
 * @param {('terraform' | 'repository')} service - The service type of the component to fetch. Can be either 'terraform' or 'repository'.
 * @returns {Promise<any>} A promise that resolves to the component entity if found, otherwise throws an error.
 * @throws Will throw an error if the fetch operation is unsuccessful.
 */
  async getComponentByService(service: 'terraform' | 'repository'): Promise<any> {
    const baseUrl = await this.discoveryApi.getBaseUrl("catalog");
    const response = await fetch(`${baseUrl}/entities\?filter\=kind\=component`, {
      method: 'GET',
    });
    if (response.ok) {
      let componentType = 'terraform'
      const data = await response.json();
      if (service == 'terraform') {
        componentType = 'workflow'
      } else {
        componentType = 'repository'
      }
      const entity = data.find((e: { metadata: { name: string; }; }) => e.metadata.name.includes(service));
      return entity;
    } else {
      const errorMessage = await response.text();
      throw new Error(`Failed to get component: ${response.status} - ${errorMessage}`);
    }
  }

  async getRun(workspaceID: string, tenantId:any): Promise<RunResponse[]> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/workspaces/${workspaceID}/runs`, {
      method: 'GET',
      headers: await this.getHeaders('org_token',tenantId) 
    });
    if (!response.ok) {
      throw new Error(
        `failed to get runs, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json().then(run => {
      return run.data;
    });
  }

  async applyRun(runID: string, tenantId:any) {
    const { apiUrl } = await this.getUrls();
    const payload = {
      "comment": "Confirmed",
    };

    const response = await fetch(`${apiUrl}/runs/${runID}/actions/apply`, {
      method: 'POST',
      headers: await this.getHeaders('org_token', tenantId) ,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(
        `failed to apply a new run, status ${response.status}: ${response.statusText}`,
      );
    }
  }

  async discardRun(runID: string, tenantId:any) {
    const { apiUrl } = await this.getUrls();
    const payload = {
      "comment": "Discarded",
    };

    const response = await fetch(`${apiUrl}/runs/${runID}/actions/discard`, {
      method: 'POST',
      headers: await this.getHeaders('org_token', tenantId) ,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(
        `failed to discard a new run, status ${response.status}: ${response.statusText}`,
      );
    }
  }

  async createRun(payload: RunRequest, tenantId:any) {
    const { apiUrl } = await this.getUrls();
    payload.data.attributes['is-destroy'] = false
    const response = await fetch(`${apiUrl}/runs`, {
      method: 'POST',
      headers: await this.getHeaders('org_token', tenantId) ,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(
        `failed to create run, status ${response.status}: ${response.statusText}`,
      );
    }
  }

  async destroyRun(payload: RunRequest, tenantId:any) {
    const { apiUrl } = await this.getUrls();
    payload.data.attributes['is-destroy'] = true
    const response = await fetch(`${apiUrl}/runs`, {
      method: 'POST',
      headers: await this.getHeaders('org_token', tenantId) ,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(
        `failed to destroy run, status ${response.status}: ${response.statusText}`,
      );
    }
  }

  async updateWorkspace(workspaceID: string, payload: WorkspaceRequest, tenantId:any): Promise<Workspace> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/workspaces/${workspaceID}`, {
      method: 'PATCH',
      headers: await this.getHeaders('org_token', tenantId) ,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(
        `failed to update workspace, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getVariables(organization: string, workspaceName: string, tenantId:any): Promise<VariablesResponse> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/vars\?filter[organization][name]\=${organization}\&filter[workspace][name]\=${workspaceName}`, {
      method: 'GET',
      headers: await this.getHeaders('org_token', tenantId) 
    });

    if (!response.ok) {
      throw new Error(
        `failed to get variable, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async createVariables(payload: VariableRequest, tenantId:any): Promise<VariableResponse> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/vars`, {
      method: 'POST',
      headers: await this.getHeaders('org_token', tenantId) ,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(
        `failed to create variable, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async updateVariable(variableID: string, payload: VariableRequest, tenantId:any): Promise<VariableResponse> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/vars/${variableID}`, {
      method: 'PATCH',
      headers: await this.getHeaders('org_token', tenantId) ,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(
        `failed to update variable, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async deleteVariable(variableID: string, tenantId:any) {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/vars/${variableID}`, {
      method: 'DELETE',
      headers: await this.getHeaders('org_token', tenantId) 
    });

    if (!response.ok) {
      throw new Error(
        `failed to delete a variable, status ${response.status}: ${response.statusText}`,
      );
    }
  }
  /**
   * Fetches a list of teams for a given organization from the Terraform API.
   *
   * @param {string} organization - The name of the organization.
   * @param {string} orgToken - The organization's API token.
   * @returns {Promise<TeamsResponse>} - A promise that resolves to the response from the Terraform API, which includes a list of teams.
   * @throws Will throw an error if the fetch operation is unsuccessful.
   */
  async listTeams(organization: string, orgToken: string): Promise<TeamsResponse> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/organizations/${organization}/teams`, {
      method: 'GET',
      headers: this.generateHeaders(orgToken)
    });

    if (!response.ok) {
      throw new Error(
        `failed to get teams, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json();
  }

  /**
   * Generates a team token for a given team in the Terraform API.
   *
   * @param {string} teamID - The ID of the team.
   * @param {TeamTokenRequest} payload - The request payload.
   * @param {string} orgToken - The organization's API token.
   * @returns {Promise<TeamTokenResponse>} - A promise that resolves to the response from the Terraform API, which includes the team token.
   * @throws Will throw an error if the fetch operation is unsuccessful.
   */
  async generateTeamToken(teamID: string, payload: TeamTokenRequest, orgToken: string): Promise<TeamTokenResponse> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/teams/${teamID}/authentication-token`, {
      method: 'POST',
      headers: this.generateHeaders(orgToken),
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(
        `failed to generate team API token, status ${response.status}: ${response.statusText}`,
      );
    }
    const teamTokenResponse: TeamTokenResponse = await response.json();
    await this.saveTerraformToken(teamTokenResponse.data.attributes.token, teamID);
    return teamTokenResponse;
  }


  /**
 * Sets up the Vault for Terraform.
 *
 * @param {string} vaultRootToken - The root token of the Vault.
 * @param {string} orgToken - The organization's API token.
 * @param {string} tfCloudTeamId - The ID of the Terraform Cloud team.
 * @param {string} terraformMount - The mount point for the Terraform engine in Vault.
 * @returns {Promise<void>} - A promise that resolves when the Vault setup is complete.
 * @throws Will throw an error if the Vault setup operation is unsuccessful.
 */
  async setupVault(vaultRootToken: string, orgToken: string, tfCloudTeamId: string, terraformMount: string) {
    await this.vaultSetup(vaultRootToken, orgToken, tfCloudTeamId, terraformMount)
  }

  /**
 * Creates a new team for a given organization in the Terraform API.
 *
 * @param {string} organization - The name of the organization.
 * @param {TeamRequest} payload - The request payload for creating a new team.
 * @param {string} orgToken - The organization's API token.
 * @returns {Promise<TeamResponse>} - A promise that resolves to the response from the Terraform API, which includes the details of the newly created team.
 * @throws Will throw an error if the fetch operation is unsuccessful.
 */
  async createNewTeam(organization: string, payload: TeamRequest, orgToken: string): Promise<TeamResponse> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/organizations/${organization}/teams`, {
      method: 'POST',
      headers: this.generateHeaders(orgToken),
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      throw new Error(
        `failed to create new Terraform team, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json();
  }

  /**
   * Fetches the organization information from the Terraform API.
   *
   * @param {string} orgToken - The organization's API token.
   * @returns {Promise<OrganizationResponse>} - A promise that resolves to the response from the Terraform API, which includes the organization information.
   * @throws Will throw an error if the fetch operation is unsuccessful.
   */
  async getOrganization(orgToken: string): Promise<OrganizationResponse> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/organizations`, {
      method: 'GET',
      headers: this.generateHeaders(orgToken),
    });
    if (!response.ok) {
      throw new Error(
        `failed to get organization information, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json();
  }

  /**
 * Saves the organization details to show on header in the Vault.
 *
 * @param {string} orgDetail - The organization's API token.
 * @returns {Promise<void>} - A promise that resolves when the organization token is successfully saved in the Vault.
 * @throws Will throw an error if the fetch operation is unsuccessful.
 */
  async saveOrgDetails(orgDetail:string){
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const vaultProxyUrl = `${proxyUrl}/vault`;
    const vaultHeaders = new Headers();
    const vaultToken = await this.getVaultToken();
    vaultHeaders.append("X-Vault-Token", `${vaultToken}`);
    vaultHeaders.append("Content-Type", "application/json");
    const response = await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
      method: 'GET',
      headers: vaultHeaders,
    });
    const data = await response.json().then((resp: GlobalSecretResponse) => {
      return resp.data;
    });
    data["org_detail"] = orgDetail;
    await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
      method: 'POST',
      headers: vaultHeaders,
      body: JSON.stringify(data)
    })

  }

   /**
 * Fetches the organization token from the Vault.
 *
 * @async
 * @returns {Promise<OrgDetailResponse>} - A promise that resolves to the response from the Vault, which includes the organization token.
 * @throws Will throw an error if the fetch operation is unsuccessful.
 */
   async fetchOrgDetails(tenantId): Promise<OrgDetailResponse> {
    // const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    // const vaultProxyUrl = `${proxyUrl}/vault`;
    // const vaultHeaders = new Headers();
    // const vaultToken = await this.getVaultToken();
    // vaultHeaders.append("X-Vault-Token", `${vaultToken}`);
    // vaultHeaders.append("Content-Type", "application/json");
    // const response = await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
    //   method: 'GET',
    //   headers: vaultHeaders,
    // });
    // const data = await response.json().then((resp: GlobalSecretResponse) => {
    //   return resp.data;
    // });
    const data = await this.getSecrets('org_detail', tenantId);
    return {
      data: {
        orgDetail: data.data || null,
      }
    } as OrgDetailResponse
  }


  /**
 * Saves the organization token in the Vault.
 *
 * @param {string} orgToken - The organization's API token.
 * @returns {Promise<void>} - A promise that resolves when the organization token is successfully saved in the Vault.
 * @throws Will throw an error if the fetch operation is unsuccessful.
 */
  async saveOrgToken(orgToken: string) {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const vaultProxyUrl = `${proxyUrl}/vault`;
    const vaultHeaders = new Headers();
    const vaultToken = await this.getVaultToken();
    vaultHeaders.append("X-Vault-Token", `${vaultToken}`);
    vaultHeaders.append("Content-Type", "application/json");
    const response = await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
      method: 'GET',
      headers: vaultHeaders,
    });
    const data = await response.json().then((resp: GlobalSecretResponse) => {
      return resp.data;
    });
    data["org_token"] = orgToken;
    await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
      method: 'POST',
      headers: vaultHeaders,
      body: JSON.stringify(data)
    })
  }

  /**
 * Fetches the organization token from the Vault.
 *
 * @async
 * @returns {Promise<OrgTokenResponse>} - A promise that resolves to the response from the Vault, which includes the organization token.
 * @throws Will throw an error if the fetch operation is unsuccessful.
 */
  async fetchOrgToken(): Promise<OrgTokenResponse> {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const vaultProxyUrl = `${proxyUrl}/vault`;
    const vaultHeaders = new Headers();
    const vaultToken = await this.getVaultToken();
    vaultHeaders.append("X-Vault-Token", `${vaultToken}`);
    vaultHeaders.append("Content-Type", "application/json");
    const response = await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
      method: 'GET',
      headers: vaultHeaders,
    });
    const data = await response.json().then((resp: GlobalSecretResponse) => {
      return resp.data;
    });
    return {
      data: {
        orgToken: data['org_token'] || null,
      }
    } as OrgTokenResponse
  }

  /**
 * Fetches the team token from the Vault.
 *
 * @async
 * @returns {Promise<TeamTokenVaultResponse>} - A promise that resolves to the response from the Vault, which includes the team token and team ID.
 * @throws Will throw an error if the fetch operation is unsuccessful.
 */
  async fetchTeamToken(): Promise<TeamTokenVaultResponse> {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const vaultProxyUrl = `${proxyUrl}/vault`;
    const vaultHeaders = new Headers();
    const vaultToken = await this.getVaultToken();
    vaultHeaders.append("X-Vault-Token", `${vaultToken}`);
    vaultHeaders.append("Content-Type", "application/json");
    const response = await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
      method: 'GET',
      headers: vaultHeaders,
    });
    const data = await response.json().then((resp: GlobalSecretResponse) => {
      return resp.data;
    });
    return {
      data: {
        teamId: data.team_id || null,
        teamToken: data.team_token || null,
      }
    } as TeamTokenVaultResponse
  }

  async deleteOrgToken() {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const vaultProxyUrl = `${proxyUrl}/vault`;
    const vaultHeaders = new Headers();
    const vaultToken = await this.getVaultToken();
    vaultHeaders.append("X-Vault-Token", `${vaultToken}`);
    vaultHeaders.append("Content-Type", "application/json");
    const response = await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
      method: 'GET',
      headers: vaultHeaders,
    });
    const data = await response.json().then((resp: GlobalSecretResponse) => {
      return resp.data;
    });
    const { org_token, ...rest } = data;
    await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
      method: 'POST',
      headers: vaultHeaders,
      body: JSON.stringify(rest)
    })
  }

  async saveCredentials(creds: BackstageCredentials) {
    // const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    // const vaultProxyUrl = `${proxyUrl}/vault`;
    // const vaultHeaders = new Headers();
    // const vaultToken = await this.getVaultToken();
    // vaultHeaders.append("X-Vault-Token", `${vaultToken}`);
    // vaultHeaders.append("Content-Type", "application/json");
    // const response = await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
    //   method: 'GET',
    //   headers: vaultHeaders,
    // });
    // const data = await response.json().then((resp: GlobalSecretResponse) => {
    //   return resp.data;
    // });
    // const data = await this.getSecrets();
    // console.log(`data is ${creds.access_key}`)
    // data["org_token"] = creds.org_token 
    // data["access_key"] = creds.access_key 
    // data["secret_key"] = creds.secret_key 
    // data["team_token"] = creds.team_token 
    // data["team_id"] = creds.team_id 
    // data["github_token"] = creds.github_token 
    // data["github_token_name"] = creds.github_token_name 
    await this.saveSecrets(creds);
  }

  async fetchCredentials(): Promise<BackstageCredentialsResponse> {

    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    const vaultProxyUrl = `${proxyUrl}/vault`;
    const vaultHeaders = new Headers();
    const vaultToken = await this.getVaultToken();
    vaultHeaders.append("X-Vault-Token", `${vaultToken}`);
    vaultHeaders.append("Content-Type", "application/json");
    const response = await fetch(`${vaultProxyUrl}/v1/secret/creds`, {
      method: 'GET',
      headers: vaultHeaders,
    });
    const data = await response.json().then((resp: GlobalSecretResponse) => {
      return resp.data;
    });
    return {
      data: {
        team_id: data.team_id || null,
        team_token: data.team_token || null,
        org_token: data.org_token || null,
        github_token: data.github_token || null,
        access_key: data.access_key || null,
        secret_key: data.secret_key || null,
        github_token_name: data.github_token_name || null,
        jenkin_server: data.jenkin_server || null,
        jenkin_server_username: data.jenkin_server_username || null,
        jenkin_server_password: data.jenkin_server_password || null,
        jenkin_server_apitoken: data.jenkin_server_apitoken || null,
      }
    } as BackstageCredentialsResponse
  }

  async getTeamDetail(teamToken: string, teamId: string): Promise<TeamResponse> {
    const { apiUrl } = await this.getUrls();

    const response = await fetch(`${apiUrl}/teams/${teamId}`, {
      method: 'GET',
      headers: this.generateHeaders(teamToken),
    });
    if (!response.ok) {
      throw new Error(
        `failed to get team information, status ${response.status}: ${response.statusText}`,
      );
    }

    return response.json();
  }
}
